package com.neurobs.react

import com.neurobs.errors.{Result, toResult}
import com.neurobs.util.PlainDate
import slinky.core.facade.{React, ReactRef}

import scala.util.{Failure, Success, Try}

trait SingleValueControlWrapper[T] {
	def value: T
	def setValue(newValue: T): Unit
}

case class SingleValueControlWrapperTest[T](var value: T) extends SingleValueControlWrapper[T] {
	override def setValue(newValue: T): Unit = { value = newValue }
}

abstract class SingleValueControlWrapperReactBase[ValueType, ControlType](var valueCache: ValueType) extends SingleValueControlWrapper[ValueType] {
	val ref: ReactRef[ControlType] = React.createRef[ControlType]

	private def get: Option[ControlType] = if (ref.current == null) None else Some(ref.current)

	protected def controlValue(control: ControlType): ValueType
	protected def setControlValue(control: ControlType, newValue: ValueType): Unit

	override def value: ValueType = get match {
		case None => valueCache
		case Some(c) => controlValue(c)
	}
	override def setValue(newValue: ValueType): Unit = {
		valueCache = newValue
		get.foreach(setControlValue(_, newValue))
	}
}

trait StringValueControlWrapper extends SingleValueControlWrapper[String] {
	def exactValue: String = value
	def setExactValue(newValue: String): Unit = setValue(newValue)

	def getValue(stripWhitespace: Boolean = true): String = if (stripWhitespace) exactValue.trim else exactValue

	/** Returns None for empty strings or Some(str) otherwise */
	def optionValue(stripWhitespace: Boolean = true): Option[String] = {
		val v = getValue(stripWhitespace)
		if (v.isEmpty) None else Some(v)
	}

	def getNonEmpty(name: String): Result[String] = {
		toResult(optionValue(), noValueMessage = s"The $name is required.")
	}

	/** Returns value converted to an integer (if possible) which satisfies an optional condition */
	def intValue(condition: Int => Boolean = _ => true): Option[Int] = Try(getValue(stripWhitespace = true).toInt).toOption.flatMap(v => if (condition(v)) Some(v) else None)

	def getDate(name: String): Result[PlainDate] = {
		Try(java.time.LocalDate.parse(getValue())) match {
			case Failure(_) => Left(s"The $name must be a date in format YYYY-MM-DD.")
			case Success(d) => Right(PlainDate(d))
		}
	}
}